import React from 'react';

/**
 * PageHero — FDJ-style navy banner that extends the navbar look
 * onto every page. Solid navy gradient background, white headline
 * with optional gold accent on the highlighted word, white/grey
 * subtitle, gold icon disc on the left.
 *
 * Props:
 *   icon      — Lucide icon component (e.g. Trophy, Coins, Bot)
 *   title     — main heading text (white)
 *   highlight — optional accent word or fragment (rendered in gold)
 *   subtitle  — supporting copy under the title
 *   right     — optional ReactNode rendered at the right (badge, stat...)
 *   testid    — data-testid on the outer banner
 */
export const PageHero = ({ icon: Icon, title, highlight, subtitle, right, testid }) => {
    return (
        <div
            data-testid={testid || 'page-hero'}
            className="page-hero-banner relative overflow-hidden mb-8 px-4 sm:px-6 lg:px-8 py-7 sm:py-9"
            style={{
                background: 'linear-gradient(135deg, #003B71 0%, #002A52 50%, #003B71 100%)',
                borderBottom: '3px solid #FFD800',
            }}
        >
            {/* Subtle decorative gold streak */}
            <div
                aria-hidden="true"
                className="absolute inset-y-0 right-0 w-1/3 opacity-20 pointer-events-none"
                style={{
                    background: 'radial-gradient(circle at 80% 50%, #FFD800 0%, transparent 60%)',
                }}
            />
            <div className="relative max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4 min-w-0">
                    {Icon && (
                        <div
                            className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shadow-lg"
                            style={{
                                background: 'rgba(255, 216, 0, 0.18)',
                                border: '1px solid rgba(255, 216, 0, 0.45)',
                            }}
                        >
                            <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: '#FFD800' }} />
                        </div>
                    )}
                    <div className="min-w-0">
                        <h1
                            className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight"
                            style={{ color: '#FFFFFF', letterSpacing: '-0.01em' }}
                        >
                            {title}
                            {highlight && (
                                <>
                                    {' '}
                                    <span style={{ color: '#FFD800' }}>{highlight}</span>
                                </>
                            )}
                        </h1>
                        {subtitle && (
                            <p
                                className="mt-1 text-sm sm:text-base font-medium"
                                style={{ color: 'rgba(255,255,255,0.78)' }}
                            >
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>
                {right && <div className="flex-shrink-0">{right}</div>}
            </div>
        </div>
    );
};

export default PageHero;
